import type { ObjectEntryPermissionGuard } from "./permission-guard";
import type { IFieldPinMap } from "./pin-field";
import type { IFieldClimate } from "./climate-field";
import type { ICondition, TFieldValue } from "./utils";

// oxlint-disable-next-line no-shadow
export enum IFieldTypes {
  "matrixField" = "matrixField",
  "pinMap" = "pinMap",
  "climate" = "climate",
  "dateField" = "dateField",
  "timeField" = "timeField",
  "textArea" = "textArea",
  "textField" = "textField",
  "audioNote" = "audioNote",
  "phoneField" = "phoneField",
  "emailField" = "emailField",
  "signatureField" = "signatureField",
  "numberField" = "numberField",
  "toggle" = "toggle",
  "checkbox" = "checkbox",
  "password" = "password",
  "timezone" = "timezone",
  "autocomplete" = "autocomplete",
  "asyncAutocompleteField" = "asyncAutocompleteField",
  "select" = "select",
  "radio" = "radio",
  "videoPlayer" = "videoPlayer",
  "colorField" = "colorField",
  "userSelectField" = "userSelectField",
  "roleSelectField" = "roleSelectField",
  "documentSelectField" = "documentSelectField",
  "fileSelect" = "fileSelect",
  "imageSelect" = "imageSelect",
  "siteSelectField" = "siteSelectField",
  "videoSelect" = "videoSelect",
  "inspectionQuestion" = "inspectionQuestion",
  "subForm" = "subForm",
  "bigToggleField" = "bigToggleField",
  "contractorSelectField" = "contractorSelectField",
  "equipmentSelectField" = "equipmentSelectField",
  "projectSelectField" = "projectSelectField",
  "wysiwyg" = "wysiwyg",
  "weekdaySelect" = "weekdaySelect",
}

export * from "./pin-field";
export * from "./climate-field";

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

// oxlint-disable-next-line no-shadow
export enum IElementTypes {
  "divider" = "divider",
  "header" = "header",
  "text" = "text",
  "textEditor" = "textEditor",
}

/**
 * Type expected for Lookup (Select) filed options
 */
export interface IFieldOptions<T = Record<string, unknown>> {
  key?: string;
  label: string;
  value: string | number | boolean | null;
  meta?: T;
}

/**
 * Shared metadata for field definitions and field references (per-form overrides).
 */
export interface IFieldMetadata {
  displayInNotification?: boolean;
  displayInTodo?: boolean;
  [key: string]: unknown;
}

/**
 * Reference to a field definition.
 * Within the field/group configuration you can reuse field configs and give them new names/labels
 */

export interface IFieldReference {
  name: string;
  label: string;
  fieldId: string;
  displayInNotification?: boolean;
  span?: IFormElementBase["span"];
  description?: string;
  disabled?: boolean;
  hidden?: boolean;
  readonly?: boolean;
  required?: boolean;

  /**
   * Add permission level check to the fieldGroup
   * - This will only be useful when used within a form, object, or training
   */
  permissionGuard?: ObjectEntryPermissionGuard[];

  // conditions that must be met in order to render this group
  conditions?: ICondition[];

  /**
   * Form-specific metadata for the field reference.
   * Allows customizing behavior per-form (e.g., displayInTodo).
   */
  meta?: IFieldMetadata;
}

export function isFieldReference(field?: unknown): field is IFieldReference {
  return (
    isObject(field) &&
    Object.getOwnPropertyNames(field).includes("name") &&
    Object.getOwnPropertyNames(field).includes("label") &&
    Object.getOwnPropertyNames(field).includes("fieldId") &&
    Boolean(field.fieldId)
  );
}

export const isField = (obj: unknown): obj is IField => {
  return Boolean(
    isObject(obj) &&
      obj.fieldType &&
      Object.values(IFieldTypes as Record<string, unknown>).includes(
        obj.fieldType,
      ),
  );
};

export const isElement = (obj: unknown): obj is IElement => {
  return Boolean(
    isObject(obj) &&
      obj.fieldType &&
      Object.values(IElementTypes as Record<string, unknown>).includes(
        obj.fieldType,
      ),
  );
};

/**
 * Form Field configuration. used to dynamically render a field within a form within the react app.
 * This structure gets saved in the database within a FieldSchema or a FieldGroupSchema
 */
export interface IFormElementBase {
  name: string;
  conditions?: ICondition[];
  permissionGuard?: ObjectEntryPermissionGuard[];
  hidden?: boolean;
  span?: 1 | 2 | 3 | 4;
  disabled?: boolean;
  readonly?: boolean;
}

export interface IFieldBase extends IFormElementBase {
  label: string;
  labelPlacement?: "inline" | "above";
  defaultValue?: TFieldValue | string[];
  hidden?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  // not strictly required for select
  required?: boolean;
  description?: string;

  /** Legacy text-field setting, retained for existing saved schemas. */
  enableAudioRecording?: boolean;

  autoFocus?: boolean;
  displayInNotification?: boolean;
  // max length or number of selected options
  min?: number;
  // min length or number of selected options
  max?: number;

  meta?: IFieldMetadata;
}

export type IFieldAutocomplete = IFieldBase & {
  fieldType: IFieldTypes.autocomplete;
  options: IFieldOptions[];
  freeSolo?: boolean;
  multiple?: boolean;
};

export type IFieldAsyncAutocomplete = IFieldBase & {
  fieldType: IFieldTypes.asyncAutocompleteField;
  multiple?: boolean;
  queryFn?: (input?: string) => Promise<IFieldOptions[]>;
  queryOptions?: () => Promise<IFieldOptions[]>;
};

export type IElementText = IFormElementBase & {
  fieldType: IElementTypes.text;
  text?: string;
  disabled?: boolean;
  readonly?: boolean;
};
export type IElementWysiwygEditor = IFormElementBase & {
  fieldType: IElementTypes.textEditor;
  documentObject: string;
  disabled?: boolean;
  readonly?: boolean;
  onChange?: (documentObject: object) => void;
};
export type IFieldWysiwygEditor = IFieldBase & {
  fieldType: IFieldTypes.wysiwyg;
  initialJSONString?: string;
  editorType?: "comment" | "full";
};

// oxlint-disable-next-line no-shadow
export enum HeaderOptions {
  "h1" = "h1",
  "h2" = "h2",
  "h3" = "h3",
  "h4" = "h4",
}

export type IElementHeader = IFormElementBase & {
  fieldType: IElementTypes.header;
  text?: string;
  meta?: { headerType: HeaderOptions; [s: string]: unknown };
  disabled?: boolean;
  readonly?: boolean;
};

export type IFieldTextArea = IFieldBase & {
  fieldType: IFieldTypes.textArea;
  minRows?: number;
};

export type IFieldUserSelect = Omit<
  IFieldAutocomplete,
  "fieldType" | "options"
> & {
  fieldType: IFieldTypes.userSelectField;
  includeInactive?: boolean;
  limitSelectionToUsers?: string[];
  limitSelectionToRoles?: string[];
  selectedStyle?: "avatar" | "name";
};

export type IFieldRoleSelect = Omit<
  IFieldAutocomplete,
  "fieldType" | "options"
> & {
  fieldType: IFieldTypes.roleSelectField;
  limitSelectionToRoles?: string[];
  includeIds?: string[];
  includeInactive?: boolean;
  selectedStyle?: "avatar" | "name";
};

export interface IDocumentFieldOptions
  extends IFieldOptions<Record<string, unknown>> {
  isFolder?: boolean | null;
  level?: number;
}

export type IFieldDocumentSelect = Omit<
  IFieldAutocomplete,
  "fieldType" | "options"
> & {
  fieldType: IFieldTypes.documentSelectField;
  options?: IDocumentFieldOptions[];
};

export type IFieldContractorSelect = Omit<
  IFieldAutocomplete,
  "fieldType" | "options"
> & {
  fieldType: IFieldTypes.contractorSelectField;
};

export type IFieldEquipmentSelect = Omit<
  IFieldAutocomplete,
  "fieldType" | "options"
> & {
  fieldType: IFieldTypes.equipmentSelectField;
  options?: IFieldOptions[];
  /** Optional equipment-type filter; empty or omitted allows all types. */
  typeFilters?: string[];
};

export type IFieldSiteSelect = Omit<
  IFieldAutocomplete,
  "fieldType" | "options" | "freeSolo"
> & {
  fieldType: IFieldTypes.siteSelectField;
};

export type IFieldProjectSelect = Omit<
  IFieldAutocomplete,
  "fieldType" | "options"
> & {
  fieldType: IFieldTypes.projectSelectField;
  /** Optional job-type filter; empty or omitted allows all job types. */
  projectConfigFilters?: string[];
  showJobModal?: boolean;
  showJobHistory?: boolean;
  /** Optional template filter; empty or omitted means all forms and tasks. */
  jobHistorySchemaFilters?: string[];
};

export type IFieldSelect =
  | (IFieldBase & {
      fieldType: IFieldTypes.select;
      options: IFieldOptions[];
      multiple?: boolean;
      defaultValue?: TFieldValue | TFieldValue[];
      sortOrder?: "desc" | "asc" | "unordered";
    })
  | (IFieldBase & {
      fieldType: IFieldTypes.select;
      options: IFieldOptions[];
      multiple?: true;
      defaultValue?: TFieldValue[];
      sortOrder?: "desc" | "asc" | "unordered";
    });

export type IFieldWeekdaySelect = Omit<
  IFieldSelect,
  "fieldType" | "options"
> & {
  fieldType: IFieldTypes.weekdaySelect;
};

export type IFieldBigToggle = IFieldBase & {
  fieldType: IFieldTypes.bigToggleField;
  options: IFieldOptions[];
};

export type IFieldRadio = IFieldBase & {
  fieldType: IFieldTypes.radio;
  defaultValue?: TFieldValue;
  options: IFieldOptions[];
  row?: boolean;
};

export type IFieldDate = IFieldBase & {
  fieldType: IFieldTypes.dateField;
  outputFormat?: string;
  minDate?: Date | "now";
  maxDate?: Date;
};

export type IFieldTime = IFieldBase & {
  fieldType: IFieldTypes.timeField;
  outputFormat?: string;
};

export type IFieldFileSelect = IFieldBase & {
  fieldType: IFieldTypes.fileSelect;
  accept?: string;
  multiple?: boolean;
};

export type IFieldVideoSelect = IFieldBase & {
  fieldType: IFieldTypes.videoSelect;
};

export type IFieldColorSelect = IFieldBase & {
  fieldType: IFieldTypes.colorField;
  /**
   * strict color picket will not include a text filed for arbitrary colors
   */
  strict?: boolean;
  colors?: (string | null)[];
  inline?: boolean;
};

export type IFieldImageSelect = IFieldBase & {
  fieldType: IFieldTypes.imageSelect;
  multiple?: boolean;
};

export type InspectionQuestionField = IFieldBase & {
  fieldType: IFieldTypes.inspectionQuestion;
  options: IFieldOptions[];
  allowActionCreation?: boolean;
  allowedCorrectiveActions?: string[];
  allowComment?: boolean;
  allowFile?: boolean;
};

export type IVideoField = IFieldBase & {
  fieldType: IFieldTypes.videoPlayer;
  metadata?: {
    videoManager?: string;
  };
  videoItemId?: string;
  videoURL?: string;
  posterURL?: string;
  /** Required for internal library videos; optional for external videos. */
  playerConfig?: {
    allowPause?: boolean;
    allowRewind?: boolean;
    allowFastForward?: boolean;
    allowPlayBackSpeed?: boolean;
    allowCloseCaption?: boolean;
    allowChangeQuality?: boolean;
    allowOfflinePlay?: boolean;
  };
};

export type MatrixQuestionField = IFieldBase & {
  fieldType: IFieldTypes.matrixField;
  matrixMap: Record<string, IFieldOptions>;
  xLabel: string;
  yLabel: string;
  xAxisKeys: { key: string; label: string; description: string }[];
  yAxisKeys: { key: string; label: string; description: string }[];
};

export type ISubForm = IFieldBase & {
  fieldType: IFieldTypes.subForm;

  // Ids refer to build schemas that are selectable.
  // - If ids is unset then all are selectable
  form: {
    type: "form" | "corrective-action";
    ids?: string[];
    ref?: string;
  };
  saveSubmitWithRootForm?: boolean;
};

export type IField =
  | (IFieldBase & {
      fieldType:
        | IFieldTypes.textField
        | IFieldTypes.audioNote
        | IFieldTypes.numberField
        | IFieldTypes.signatureField
        | IFieldTypes.phoneField
        | IFieldTypes.emailField
        | IFieldTypes.toggle
        | IFieldTypes.checkbox
        | IFieldTypes.password
        | IFieldTypes.timezone;
    })
  | IFieldTextArea
  | IFieldColorSelect
  | IFieldAutocomplete
  | IFieldFileSelect
  | IFieldImageSelect
  | IFieldVideoSelect
  | IFieldDate
  | IFieldTime
  | IFieldSelect
  | IFieldWeekdaySelect
  | IFieldRadio
  | IFieldDocumentSelect
  | IFieldContractorSelect
  | IFieldProjectSelect
  | IFieldEquipmentSelect
  | IFieldSiteSelect
  | IFieldUserSelect
  | IFieldRoleSelect
  | IFieldBigToggle
  | IFieldAsyncAutocomplete
  | IFieldWysiwygEditor
  | InspectionQuestionField
  | MatrixQuestionField
  | IFieldPinMap
  | IFieldClimate
  | IVideoField
  | ISubForm;

export type IElement =
  | (IFormElementBase & {
      fieldType: IElementTypes.divider;
    })
  | IElementText
  | IElementWysiwygEditor
  | IElementHeader;

/**
 * Field Configurations override the default behavior of a field.
 * It keys of of the fieldId
 */
export interface IFieldConfiguration {
  fieldId: string;
  conditions?: ICondition[];
  options?: IFieldOptions[];
  min?: number;
  max?: number;
  defaultValue?: TFieldValue;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}
